import { officePathByBrand, STATE_CODE_BY_NAME } from '@src/lib/shared-components-web';
import type { BrandThemeConfig } from '@src/lib/shared-components-web/types';

type SchemaFieldValue = string[] | object | boolean | number | string;

type OdpSchemaObject = Record<string, SchemaFieldValue>;

type OfficeAddress = {
	line1?: string;
	address1?: string;
	city?: string;
	state?: string;
	stateCode?: string;
	zipCode?: string;
};

type OfficeLocation = {
	name?: string;
	phoneNumber?: string;
	code?: string;
	latitude?: number | string;
	longitude?: number | string;
	location?: {
		latitude?: number | string;
		longitude?: number | string;
	};
	address?: OfficeAddress;
};

type OdpHeroSection = {
	__typename?: string;
	image?: {
		image?: {
			url?: string;
		};
	};
	buttonsCollection?: {
		items?: Array<{
			buttonLink?: string;
		} | null>;
	};
};

type OdpPageData = {
	description?: string;
	contentsCollection?: {
		items?: Array<OdpHeroSection | null>;
	};
};

type OdpReviewSummary = {
	averageRating?: number | string;
	reviewCount?: number | string;
};

export const generateODPSchemaObject = (
	officeInfo: OfficeLocation[],
	config: BrandThemeConfig,
	pageData: OdpPageData,
	appDataLogoUrl: string,
	reviews?: OdpReviewSummary
): OdpSchemaObject => {
	const { features, siteURL } = config;
	const { description } = pageData;

	const officeLocation = officeInfo[0] ?? {};
	const address = officeLocation.address ?? {};
	const addressLine1 = address?.line1;
	const city = address?.city;
	const state = address?.state;
	const stateCode = state ? STATE_CODE_BY_NAME[state] || '' : '';
	const zipCode = address?.zipCode;
	const fullAddress = `${addressLine1 || ''}, ${city || ''}, ${stateCode} ${zipCode || ''}`;
	const googleMapsUrl = config.googleMapBusinessQuery.replaceAll('[address]', `${fullAddress}`);

	const officeDetailsUrl = `${siteURL}/${features.otherApps.facilityDetailsBasePath}/${officePathByBrand(
		config.themeId,
		{
			stateStrOptions: {
				stateAbbreviation: address?.stateCode || stateCode,
				state: '',
				city: city || '',
			},
			cityAddressOptions: {
				city: city ?? undefined,
				address: address?.address1 ?? addressLine1 ?? undefined,
				zip: zipCode ?? undefined,
			},
		}
	)}/`;

	const heroSection = pageData?.contentsCollection?.items?.find((item) => item?.__typename === 'OdpHero');
	const heroImageUrl = heroSection?.image?.image?.url;
	const heroImageButtonUrl = heroSection?.buttonsCollection?.items?.[0]?.buttonLink;

	const schemaObject: OdpSchemaObject = {
		'@context': 'https://schema.org',
		url: officeDetailsUrl,
		name: `${config.name} ` + (officeLocation.name || '[location-name]'),
		description: description || '[meta-description]',
		address: {
			'@type': 'PostalAddress',
			streetAddress: addressLine1 || '[Street Address]',
			addressLocality: city || '[City]',
			addressRegion: state || '[State]',
			postalCode: zipCode || '[Zip]',
			addressCountry: 'USA',
		},
		areaServed: {
			'@type': 'AdministrativeArea',
			name: `${city || '[City]'}, ${state || '[ST]'}`,
		},
		telephone: officeLocation.phoneNumber || '(877) 510-0521',
		logo: appDataLogoUrl || '[logo-url]',
		image: heroImageUrl || appDataLogoUrl || '[image-url]',
		potentialAction: {
			'@type': 'ReserveAction',
			target: heroImageButtonUrl || '/',
		},
		branchCode: officeLocation.code || '[code]',
		isAcceptingNewPatients: true,
		geo: {
			'@type': 'GeoCoordinates',
			latitude: officeLocation?.location?.latitude || officeLocation.latitude || '[lat]',
			longitude: officeLocation?.location?.longitude || officeLocation.longitude || '[long]',
		},
		hasMap: googleMapsUrl,
		aggregateRating: {
			'@type': 'AggregateRating',
			ratingValue: reviews?.averageRating ?? '[value]',
			reviewCount: reviews?.reviewCount ?? '[count]',
		},
		brand: features?.schema?.publisher,
	};

	if (features?.officeDetailsSchema?.['@type']) {
		schemaObject['@type'] = features.officeDetailsSchema['@type'];
	}

	if (features?.officeDetailsSchema?.['medicalSpecialty']) {
		schemaObject.medicalSpecialty = features.officeDetailsSchema['medicalSpecialty'];
	}

	if (features?.officeDetailsSchema?.['hasOfferCatalog']) {
		schemaObject.hasOfferCatalog = features.officeDetailsSchema['hasOfferCatalog'];
	}

	return schemaObject;
};
